/**
 * Playlist Search Page Route
 *
 * Full-page playlist search with date/time/channel filtering.
 * Displays search results in a table format with pagination.
 */

import { Component, createSignal, createMemo, createEffect, on, Show } from "solid-js";
import { createAsync, type RouteDefinition } from "@solidjs/router";
import { getMetadataHistoryQuery, getStreamingChannelsByTenantQuery } from "~/api/cms/queries";
import { env } from "~/config/env";
import { SEO } from "~/components/SEO";
import type { MetadataHistoryQueryParams, MetadataHistoryResult } from "~/types/metadataHistory";
import { logger } from "~/lib/logger/logger";
import { PlaylistSearchForm, PlaylistResultsTable } from "./components";
import type { PaginationInfo } from "./components";
import { formatDate, getTodayDate, getCurrentTime, combineDateTimeToISO } from "./utils";
import styles from "./PlaylistSearch.module.css";

export const route = {
  preload: () => {
    getStreamingChannelsByTenantQuery(env.tenantSlug);
  },
} satisfies RouteDefinition;

const RESULTS_PER_PAGE = 20;

/**
 * PlaylistSearchPage Component
 *
 * Orchestrates state management for the playlist search feature:
 * - Form state (date, time, channel selection)
 * - Search execution and result handling
 * - Pagination control
 *
 * NOTE: Uses signals + createEffect instead of createResource to avoid
 * triggering Suspense boundaries, which would cause page flashing.
 * This pattern keeps loading state isolated from Suspense.
 */
const PlaylistSearchPage: Component = () => {
  // Form state (what user is currently editing)
  const [selectedDate, setSelectedDate] = createSignal<string>(getTodayDate());
  const [selectedTime, setSelectedTime] = createSignal<string>(getCurrentTime());
  const [selectedChannel, setSelectedChannel] = createSignal<string>("");

  // Submitted search state (what was actually searched for)
  const [submittedParams, setSubmittedParams] = createSignal<MetadataHistoryQueryParams | null>(
    null
  );

  // Search result state (managed manually to avoid Suspense)
  const [searchResult, setSearchResult] = createSignal<MetadataHistoryResult | null>(null);
  const [isLoading, setIsLoading] = createSignal(false);

  // Fetch channels for dropdown (route-level data, use createAsync)
  const channels = createAsync(() => getStreamingChannelsByTenantQuery(env.tenantSlug));

  // Fetch search results when submittedParams changes
  // Using createEffect + signals instead of createResource to avoid triggering Suspense
  createEffect(
    on(submittedParams, async (params) => {
      if (!params) {
        setSearchResult(null);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        const result = await getMetadataHistoryQuery(params);
        setSearchResult(result);
      } catch (error) {
        logger.error("[PlaylistSearch] Fetch error:", error);
        setSearchResult({
          success: false,
          data: null,
          error: error instanceof Error ? error.message : "Unknown error occurred",
        });
      } finally {
        setIsLoading(false);
      }
    })
  );

  // Derived state accessors
  const hasSearched = () => submittedParams() !== null;

  const hasSearchError = () => {
    const result = searchResult();
    return result !== null && !result.success;
  };

  const searchError = () => {
    const result = searchResult();
    return result && !result.success ? result.error : null;
  };

  const searchEntries = () => {
    const result = searchResult();
    return result?.success && result.data?.entries ? result.data.entries : [];
  };

  const searchPagination = (): PaginationInfo | null => {
    const result = searchResult();
    return result?.success && result.data?.pagination ? result.data.pagination : null;
  };

  // Format search result title based on submitted params (not form state)
  const searchResultTitle = createMemo(() => {
    const params = submittedParams();
    if (!params) return "";

    const channelList = channels();
    const channel = channelList?.find((c) => c.identifier === params.channelIdentifier);
    const channelName = (channel?.displayName ?? params.channelIdentifier ?? "").toUpperCase();

    // Parse date and time from the start ISO string
    const startDate = params.start ? new Date(params.start) : new Date();
    const date = formatDate(startDate.toISOString());
    const time = startDate.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });

    return `${channelName} VOM ${date} UM ${time} UHR`;
  });

  // Build params from current form state
  const buildSearchParams = (page: number = 1): MetadataHistoryQueryParams | null => {
    if (!selectedChannel()) return null;
    return {
      channelIdentifier: selectedChannel(),
      start: combineDateTimeToISO(selectedDate(), selectedTime()),
      page,
      limit: RESULTS_PER_PAGE,
    };
  };

  // Event handlers
  const handleSearch = (e: Event) => {
    e.preventDefault();
    const params = buildSearchParams(1);
    if (params) {
      setSubmittedParams({ ...params }); // Spread to create new object reference
    }
  };

  const handleRetry = () => {
    const params = submittedParams();
    if (params) {
      // Create new reference to trigger effect
      setSubmittedParams({ ...params });
    }
  };

  const handlePreviousPage = () => {
    const current = submittedParams();
    if (current && current.page && current.page > 1) {
      setSubmittedParams({ ...current, page: current.page - 1 });
    }
  };

  const handleNextPage = () => {
    const current = submittedParams();
    if (current) {
      setSubmittedParams({ ...current, page: (current.page || 1) + 1 });
    }
  };

  // Prepare channel data for form
  const channelOptions = () =>
    (channels() || []).map((c) => ({
      identifier: c.identifier,
      displayName: c.displayName,
    }));

  return (
    <>
      <SEO
        title="Playlistsuche - Absolut Radio"
        description="Finde heraus, welche Songs auf Absolut Radio gelaufen sind. Durchsuche unsere Playlist nach Datum, Uhrzeit und Sender."
        url={`${env.siteUrl}/playlist-suche`}
        twitterCard="summary"
      />

      <main class={styles.page}>
        <PlaylistSearchForm
          channels={channelOptions()}
          selectedDate={selectedDate()}
          selectedTime={selectedTime()}
          selectedChannel={selectedChannel()}
          onDateChange={setSelectedDate}
          onTimeChange={setSelectedTime}
          onChannelChange={setSelectedChannel}
          onSubmit={handleSearch}
        />

        <Show when={hasSearched()}>
          <PlaylistResultsTable
            resultTitle={searchResultTitle()}
            isLoading={isLoading()}
            isRefetching={false}
            hasError={hasSearchError()}
            errorMessage={searchError()}
            entries={searchEntries()}
            pagination={searchPagination()}
            onRetry={handleRetry}
            onPreviousPage={handlePreviousPage}
            onNextPage={handleNextPage}
          />
        </Show>
      </main>
    </>
  );
};

export default PlaylistSearchPage;
